import { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { GET_PRODUCTS } from '../../graphql/product-queries'
import {
  GET_VARIANT_OPTION_ITEM_PRICING,
  GET_RAW_MATERIALS,
} from '../../graphql/costing-queries'
import {
  CREATE_OPTION_ITEM_INGREDIENT,
  UPDATE_OPTION_ITEM_INGREDIENT,
  DELETE_OPTION_ITEM_INGREDIENT,
} from '../../graphql/costing-mutations'
import { formatCost } from '../../lib/costingUtils'
import { RawMaterial } from '../../types/costing'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'

/* ─── Unit helpers ───────────────────────────────────────────────────────────── */

const UNIT_FAMILIES: Record<string, string[]> = {
  g:     ['g', 'kg'],
  kg:    ['g', 'kg'],
  ml:    ['ml', 'l'],
  l:     ['ml', 'l'],
  piece: ['piece'],
}

/** Conversion factors to base unit (g or ml). */
const TO_BASE: Record<string, number> = { g: 1, kg: 1000, ml: 1, l: 1000, piece: 1 }

function getCompatibleUnits(baseUnit: string): string[] {
  return UNIT_FAMILIES[baseUnit] ?? [baseUnit]
}

function convertToBaseUnit(qty: number, inputUnit: string, baseUnit: string): number {
  const inputFactor = TO_BASE[inputUnit] ?? 1
  const baseFactor  = TO_BASE[baseUnit]  ?? 1
  return (qty * inputFactor) / baseFactor
}

/** Display the stored quantity (base unit) in the most readable compatible unit. */
function displayQuantity(qty: number, baseUnit: string): string {
  if (baseUnit === 'g' && Math.abs(qty) >= 1000)  return `${qty / 1000} kg`
  if (baseUnit === 'ml' && Math.abs(qty) >= 1000) return `${qty / 1000} l`
  return `${qty} ${baseUnit}`
}

/** Pick the best display unit and convert the quantity for pre-filling edit forms. */
function smartUnitAndQty(qty: number, baseUnit: string): { unit: string; qty: string } {
  if (baseUnit === 'g' && Math.abs(qty) >= 1000)   return { unit: 'kg', qty: String(qty / 1000) }
  if (baseUnit === 'ml' && Math.abs(qty) >= 1000)  return { unit: 'l',  qty: String(qty / 1000) }
  return { unit: baseUnit, qty: String(qty) }
}

/* ─── Types ─────────────────────────────────────────────────────────────────── */

interface PricingRow {
  id: number
  option_item_id: number
  option_item_label: string
  variant_id: number | null
  variant_name: string | null
  raw_material_name: string
  raw_material_unit: string
  quantity: number
  cost_millicents: number | null
}

interface Variant {
  id: number
  name: string
  price_cents: number
}

interface OptionItemMeta {
  id: number
  label: string
}

interface PricingData {
  product_name: string
  variants: Variant[]
  option_items: OptionItemMeta[]
  rows: PricingRow[]
}

/* ─── Inline edit row ────────────────────────────────────────────────────────── */

interface EditRowProps {
  row: PricingRow
  variants: Variant[]
  colSpan: number
  onCancel: () => void
  onSaved: () => void
}

const EditRow = ({ row, variants, onCancel, onSaved }: EditRowProps) => {
  const compatibleUnits = getCompatibleUnits(row.raw_material_unit)
  const { unit: initialUnit, qty: initialQty } = smartUnitAndQty(row.quantity, row.raw_material_unit)
  const [inputUnit, setInputUnit] = useState(initialUnit)
  const [quantity, setQuantity] = useState(initialQty)
  const [variantId, setVariantId] = useState<string>(row.variant_id != null ? String(row.variant_id) : '')

  const [updateIngredient, { loading }] = useMutation(UPDATE_OPTION_ITEM_INGREDIENT, {
    onCompleted: () => { toast.success('Ingredient updated'); onSaved() },
    onError: (err) => toast.error(`Failed: ${err.message}`),
  })

  const handleSave = () => {
    const qty = parseFloat(quantity)
    if (isNaN(qty) || qty === 0) { toast.error('Enter a non-zero quantity'); return }
    const converted = convertToBaseUnit(qty, inputUnit, row.raw_material_unit)
    updateIngredient({
      variables: {
        id: row.id,
        data: { quantity: converted, variant_id: variantId ? parseInt(variantId) : null },
      },
    })
  }

  return (
    <tr className="bg-yellow-50">
      <td className="px-4 py-2 text-sm text-gray-500">{row.option_item_label}</td>
      <td className="px-4 py-2 text-sm text-gray-700">{row.raw_material_name}</td>
      <td className="px-4 py-2">
        <select value={variantId} onChange={(e) => setVariantId(e.target.value)} className="input text-sm py-1 w-full">
          <option value="">All variants</option>
          {variants.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center gap-1">
          <input type="number" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="input text-sm py-1 w-20" />
          <select value={inputUnit} onChange={(e) => setInputUnit(e.target.value)} className="input text-sm py-1 w-20">
            {compatibleUnits.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </td>
      <td className="px-4 py-2 text-sm text-gray-400">—</td>
      <td className="px-4 py-2">
        <div className="flex items-center gap-1">
          <button onClick={handleSave} disabled={loading} className="p-1 rounded text-green-600 hover:bg-green-50" title="Save">
            <CheckIcon className="h-4 w-4" />
          </button>
          <button onClick={onCancel} className="p-1 rounded text-gray-400 hover:bg-gray-100" title="Cancel">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  )
}

/* ─── Add row form ────────────────────────────────────────────────────────────── */

interface AddRowFormProps {
  optionItemId: number
  optionItemLabel: string
  variants: Variant[]
  allMaterials: RawMaterial[]
  productId: number
  onCancel: () => void
  onSaved: () => void
}

const AddRowForm = ({ optionItemId, optionItemLabel, variants, allMaterials, productId, onCancel, onSaved }: AddRowFormProps) => {
  const [rawMaterialId, setRawMaterialId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [variantId, setVariantId] = useState('')
  const [inputUnit, setInputUnit] = useState('')

  const selectedMaterial = allMaterials.find((m) => m.id === parseInt(rawMaterialId))
  const baseUnit = selectedMaterial?.unit ?? ''
  const compatibleUnits = baseUnit ? getCompatibleUnits(baseUnit) : []

  // When the raw material changes, reset inputUnit to the base unit
  const handleMaterialChange = (id: string) => {
    setRawMaterialId(id)
    const mat = allMaterials.find((m) => m.id === parseInt(id))
    setInputUnit(mat?.unit ?? '')
  }

  const [createIngredient, { loading }] = useMutation(CREATE_OPTION_ITEM_INGREDIENT, {
    refetchQueries: [{ query: GET_VARIANT_OPTION_ITEM_PRICING, variables: { productId } }],
    onCompleted: () => { toast.success('Ingredient added'); onSaved() },
    onError: (err) => toast.error(`Failed: ${err.message}`),
  })

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault()
    const qty = parseFloat(quantity)
    if (!rawMaterialId) { toast.error('Select an ingredient'); return }
    if (isNaN(qty) || qty === 0) { toast.error('Enter a non-zero quantity'); return }
    const converted = baseUnit && inputUnit ? convertToBaseUnit(qty, inputUnit, baseUnit) : qty
    const data: Record<string, unknown> = { raw_material_id: parseInt(rawMaterialId), quantity: converted }
    if (variantId) data.variant_id = parseInt(variantId)
    createIngredient({ variables: { optionItemId, data } })
  }

  return (
    <tr className="bg-blue-50">
      <td className="px-4 py-2 text-sm text-gray-500 italic">{optionItemLabel}</td>
      <td className="px-4 py-2">
        <select value={rawMaterialId} onChange={(e) => handleMaterialChange(e.target.value)} className="input text-sm py-1 w-full">
          <option value="">Select ingredient…</option>
          {allMaterials.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>)}
        </select>
      </td>
      <td className="px-4 py-2">
        <select value={variantId} onChange={(e) => setVariantId(e.target.value)} className="input text-sm py-1 w-full">
          <option value="">All variants</option>
          {variants.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center gap-1">
          <input type="number" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="200" className="input text-sm py-1 w-20" />
          {compatibleUnits.length > 1 ? (
            <select value={inputUnit} onChange={(e) => setInputUnit(e.target.value)} className="input text-sm py-1 w-20">
              {compatibleUnits.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          ) : (
            baseUnit && <span className="text-xs text-gray-400 w-10">{baseUnit}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-2" />
      <td className="px-4 py-2">
        <div className="flex items-center gap-1">
          <button onClick={handleAdd} disabled={loading} className="p-1 rounded text-green-600 hover:bg-green-50" title="Add">
            <CheckIcon className="h-4 w-4" />
          </button>
          <button onClick={onCancel} className="p-1 rounded text-gray-400 hover:bg-gray-100" title="Cancel">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  )
}

/* ─── Product section inside the shared table ───────────────────────────────── */

interface ProductSectionProps {
  productId: number
  productName: string
  canManage: boolean
  allMaterials: RawMaterial[]
}

const ProductSection = ({ productId, productName, canManage, allMaterials }: ProductSectionProps) => {
  const [editingId, setEditingId] = useState<number | null>(null)
  const [addingForOptionItem, setAddingForOptionItem] = useState<number | null>(null)

  const { data, loading, error, refetch } = useQuery(GET_VARIANT_OPTION_ITEM_PRICING, {
    variables: { productId },
    fetchPolicy: 'cache-and-network',
  })

  const [deleteIngredient] = useMutation(DELETE_OPTION_ITEM_INGREDIENT, {
    refetchQueries: [{ query: GET_VARIANT_OPTION_ITEM_PRICING, variables: { productId } }],
    onCompleted: () => toast.success('Ingredient removed'),
    onError: (err) => toast.error(`Failed: ${err.message}`),
  })

  // Product header row
  const headerRow = (
    <tr key={`product-header-${productId}`} className="bg-gray-100">
      <td colSpan={canManage ? 6 : 5} className="px-4 py-2 text-sm font-semibold text-gray-800">
        {productName}
      </td>
    </tr>
  )

  if (loading) {
    return (
      <>
        {headerRow}
        <tr>
          <td colSpan={canManage ? 6 : 5} className="px-4 py-4 text-center">
            <LoadingSpinner size="sm" />
          </td>
        </tr>
      </>
    )
  }

  if (error) {
    return (
      <>
        {headerRow}
        <tr>
          <td colSpan={canManage ? 6 : 5} className="px-4 py-3 text-sm text-red-600">{error.message}</td>
        </tr>
      </>
    )
  }

  const pricing: PricingData | null = data?.variantOptionItemPricing ?? null

  const allGrouped = (pricing?.option_items ?? []).map((oi) => ({
    optionItem: oi,
    rows: (pricing?.rows ?? []).filter((r) => r.option_item_id === oi.id),
  }))

  if (allGrouped.length === 0) {
    return (
      <>
        {headerRow}
        <tr>
          <td colSpan={canManage ? 6 : 5} className="px-4 py-3 text-sm text-gray-400 italic">
            No option items linked to this product.
          </td>
        </tr>
      </>
    )
  }

  const variants = pricing?.variants ?? []

  return (
    <>
      {headerRow}
      {allGrouped.map(({ optionItem, rows }, groupIdx) => (
        <>
          {/* Ingredient rows */}
          {rows.map((row) =>
            editingId === row.id ? (
              <EditRow
                key={row.id}
                row={row}
                variants={variants}
                colSpan={canManage ? 6 : 5}
                onCancel={() => setEditingId(null)}
                onSaved={() => { setEditingId(null); refetch() }}
              />
            ) : (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-sm text-gray-600">{row.option_item_label}</td>
                <td className="px-4 py-2.5 text-sm text-gray-800">
                  {row.raw_material_name}
                  <span className="ml-1 text-xs text-gray-400">({row.raw_material_unit})</span>
                </td>
                <td className="px-4 py-2.5 text-sm">
                  {row.variant_id != null ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                      {row.variant_name ?? `#${row.variant_id}`}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">All variants</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap">
                  <span className={row.quantity < 0 ? 'text-red-600' : ''}>
                    {row.quantity > 0 ? '+' : ''}{displayQuantity(row.quantity, row.raw_material_unit)}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap">
                  {formatCost(row.cost_millicents)}
                </td>
                {canManage && (
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { setAddingForOptionItem(null); setEditingId(row.id) }}
                        className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                        title="Edit"
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => deleteIngredient({ variables: { id: row.id } })}
                        className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                        title="Delete"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            )
          )}

          {/* Add form */}
          {canManage && addingForOptionItem === optionItem.id && (
            <AddRowForm
              key={`add-${optionItem.id}`}
              optionItemId={optionItem.id}
              optionItemLabel={optionItem.label}
              variants={variants}
              allMaterials={allMaterials}
              productId={productId}
              onCancel={() => setAddingForOptionItem(null)}
              onSaved={() => setAddingForOptionItem(null)}
            />
          )}

          {/* Add button row */}
          {canManage && addingForOptionItem !== optionItem.id && (
            <tr key={`add-btn-${optionItem.id}`} className="bg-white">
              <td className="px-4 py-1.5 text-xs text-gray-400">
                {rows.length === 0 ? optionItem.label : ''}
              </td>
              <td colSpan={canManage ? 5 : 4} className="px-4 py-1.5">
                <button
                  onClick={() => { setEditingId(null); setAddingForOptionItem(optionItem.id) }}
                  className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 hover:underline"
                >
                  <PlusIcon className="h-3 w-3" />
                  Add ingredient for {optionItem.label}
                </button>
              </td>
            </tr>
          )}

          {/* Thin separator between option item groups */}
          {groupIdx < allGrouped.length - 1 && (
            <tr key={`sep-oi-${optionItem.id}`} className="h-px">
              <td colSpan={canManage ? 6 : 5} className="bg-gray-100 p-0" />
            </tr>
          )}
        </>
      ))}

      {/* Thick separator after each product */}
      <tr key={`sep-product-${productId}`} className="h-2">
        <td colSpan={canManage ? 6 : 5} className="bg-gray-200 p-0" />
      </tr>
    </>
  )
}

/* ─── Page ──────────────────────────────────────────────────────────────────── */

const OptionItemCostsPage = () => {
  const { hasPermission } = usePermissions()
  const canManage = hasPermission(PERMISSIONS.COSTING_MANAGE)

  const [activeOnly, setActiveOnly] = useState(true)

  const { data: productsData, loading: productsLoading, error: productsError } = useQuery(GET_PRODUCTS, {
    variables: { filters: { active: activeOnly || undefined } },
  })

  const { data: materialsData } = useQuery(GET_RAW_MATERIALS, {
    variables: { filters: { active: true } },
    skip: !canManage,
  })

  const products: any[] = productsData?.products ?? []
  const allMaterials: RawMaterial[] = materialsData?.rawMaterials ?? []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Option Item Costs</h1>
        <p className="mt-1 text-sm text-gray-500">
          Ingredient entries per option item, broken down by variant scope.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
        <input
          type="checkbox"
          checked={activeOnly}
          onChange={(e) => setActiveOnly(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600"
        />
        Active products only
      </label>

      {productsLoading ? (
        <div className="flex items-center justify-center h-48">
          <LoadingSpinner size="lg" />
        </div>
      ) : productsError ? (
        <div className="text-center py-12 text-red-600">{productsError.message}</div>
      ) : products.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center">
          <p className="text-sm text-gray-500">No products found.</p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-44">Option Item</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">Variant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">Quantity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Cost</th>
                {canManage && <th className="relative px-4 py-3 w-20"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody className="bg-white">
              {products.map((p: any) => (
                <ProductSection
                  key={p.id}
                  productId={p.id}
                  productName={p.name}
                  canManage={canManage}
                  allMaterials={allMaterials}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default OptionItemCostsPage
